
import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  CircularProgress,
  Backdrop,
} from "@mui/material";
import { Deal } from "../../../validation/customer.validation";
import { getTermSheetForDeal } from "../../../services/customer.service";
import { getLoginUserData } from "../../../services/account.service";
import { ROLES } from "../../../constants/general.constant";
import { formatCurrency } from "../../../helper/currencyFormatter.helper";

interface Props {
  deal: Deal;
  bankerId?: string | null;
}

const TermSheetView: React.FC<Props> = ({ deal, bankerId }) => {
  const [loading, setLoading] = useState(false);
  const [termSheet, setTermSheet] = useState<any>(null);
  const [userRole, setUserRole] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const user = await getLoginUserData();
        setUserRole(user?.role || null);
      } catch (error) {
        console.error('Error fetching user role:', error);
      }

      if (!deal?.id || !bankerId) return;

      setLoading(true);
      try {
        const data = await getTermSheetForDeal(deal.id, bankerId);
        setTermSheet(data);
      } catch (error) {
        console.error('Error fetching term sheet:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [deal, bankerId]);

  if (loading) {
    return (
      <Backdrop sx={{ color: '#fff', zIndex: 1400 }} open={loading}>
        <CircularProgress color="primary" />
      </Backdrop>
    );
  }

  if (!termSheet) {
  return (
    <Box p={3} sx={{ maxHeight: '70vh', overflowY: 'auto' }}>
        <Typography variant="h6" color="textSecondary">
          Term Sheet not available yet.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxHeight: '70vh', overflowY: 'auto', display: 'flex', justifyContent: 'center' }}>
      <Box sx={{ width: '100%' }}>
        <TableContainer component={Paper} sx={{ boxShadow: 3, borderRadius: 2 }}>
          <Table>
            <TableBody>
              {termSheet.facilityType && (
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold", backgroundColor: "#f5f5f5", width: '40%' }}>
                    Facility Type
                  </TableCell>
                  <TableCell>{termSheet.facilityType}</TableCell>
                </TableRow>
              )}
              {termSheet.loanAmount && (
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}>
                    Loan Amount
                  </TableCell>
                  <TableCell>{formatCurrency(termSheet.loanAmount)}</TableCell>
                </TableRow>
              )}
              {termSheet.tenorMaturity && (
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}>
                    Tenor/Maturity
                  </TableCell>
                  <TableCell>{termSheet.tenorMaturity}</TableCell>
                </TableRow>
              )}
              {termSheet.repaymentSchedule && (
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}>
                    Repayment Schedule
                  </TableCell>
                  <TableCell>{termSheet.repaymentSchedule}</TableCell>
                </TableRow>
              )}
              {termSheet.pricing && (
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}>
                    Pricing
                  </TableCell>
                  <TableCell>{termSheet.pricing}</TableCell>
                </TableRow>
              )}
              {termSheet.fees && (
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}>
                    Fees
                  </TableCell>
                  <TableCell>{termSheet.fees}</TableCell>
                </TableRow>
              )}
              {termSheet.collateral && (
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}>
                    Collateral
                  </TableCell>
                  <TableCell>{termSheet.collateral}</TableCell>
                </TableRow>
              )}
              {termSheet.loanType && (
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}>
                    Loan Type
                  </TableCell>
                  <TableCell>{termSheet.loanType}</TableCell>
                </TableRow>
              )}
              {termSheet.sbaType && termSheet.loanType === 'sba' && (
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}>
                    SBA Type
                  </TableCell>
                  <TableCell>{termSheet.sbaType}</TableCell>
                </TableRow>
              )}
              {userRole !== ROLES.CUSTOMER && termSheet.sbaFee && termSheet.loanType && termSheet.loanType === 'sba' && (
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}>
                    SBA Fee %
                  </TableCell>
                  <TableCell>{termSheet.sbaFee || "-"}%</TableCell>
                </TableRow>
              )}
              {userRole !== ROLES.CUSTOMER && termSheet.sbaCap && termSheet.loanType && termSheet.loanType === 'sba' && (
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}>
                    CAP Amount
                  </TableCell>
                  <TableCell>${termSheet.sbaCap || '-'}</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
};

export default TermSheetView;
